import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { useUrlState } from '../nav/urlState';
import { dropDraft } from '../nav/drafts';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { HistoryPanel } from '../history/HistoryPanel';
import { leaveGuarded } from '../ui/unsaved';
import { RowMenu } from '../ui/RowMenu';
import { ContractorForm, DeleteDialog, CONTRACTORS_KEY, KIND_LABELS, bpToPercent, type Contractor, type Kind } from './ContractorForm';
import { dropOn, moveBy } from '../quotes/lineOrder';
import { ContractorInfo, infoAvailable } from './ContractorInfo';
import './contractors.css';

const RESOURCE = 'contractors.contractors';
type ListResponse = { contractors: Contractor[]; truncated: boolean };

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/**
 * The manual order (owner 2026-10-23 19:27: „mozliwosc przesuwania ich na liscie gora dol”): ↑ / ↓ and dragging ⋮⋮, as
 * the quote's lines (#112). The new order shows at once; the moves are saved one after another, each with the order
 * the server has (`before`), so quick presses do not clash. What a search or the filter shows is reordered among its
 * own places (the server keeps the others where they are). A clash (someone else moved) reloads the list.
 */
function useOrder(served: Contractor[] | undefined) {
  const queryClient = useQueryClient();
  const [local, setLocal] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const [drag, setDrag] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<{ id: string; after: boolean } | null>(null);
  const confirmed = useRef<string[]>([]);
  const pending = useRef(0);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const serverIds = (served ?? []).map((c) => c.id).join();
  useEffect(() => {
    if (pending.current > 0) return;
    confirmed.current = serverIds === '' ? [] : serverIds.split(',');
    setLocal(null);
  }, [serverIds]);
  const byId = new Map((served ?? []).map((c) => [c.id, c]));
  const ids = local && local.length === byId.size && local.every((id) => byId.has(id)) ? local : (served ?? []).map((c) => c.id);
  const items = ids.map((id) => byId.get(id)!);

  function save(next: string[] | null) {
    if (!next) return;
    setLocal(next);
    setError('');
    pending.current++;
    chain.current = chain.current.then(async () => {
      try {
        const before = confirmed.current;
        if (before.join() !== next.join()) {
          await api('/api/contractors/order', { method: 'PUT', body: { ids: next, before } });
          confirmed.current = next;
        }
      } catch (e) {
        setError(e instanceof ApiError && e.status === 409 ? 'Ktoś w międzyczasie zmienił kolejność. Lista wczytała się od nowa: ustaw kolejność jeszcze raz.' : e instanceof ApiError ? e.message : 'Nie udało się zmienić kolejności');
        pending.current = 0;
        setLocal(null);
        await queryClient.invalidateQueries({ queryKey: CONTRACTORS_KEY });
        return;
      } finally {
        if (pending.current > 0) pending.current--;
      }
    });
  }
  return {
    items,
    error,
    drag,
    dropAt,
    setDrag,
    move: (id: string, delta: -1 | 1) => save(moveBy(ids, id, delta)),
    dragOver(e: DragEvent<HTMLLIElement>, id: string) {
      if (!drag) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const box = e.currentTarget.getBoundingClientRect();
      const after = e.clientY > box.top + box.height / 2;
      if (dropAt?.id !== id || dropAt.after !== after) setDropAt({ id, after });
    },
    drop(e: DragEvent<HTMLLIElement>, id: string) {
      if (!drag) return;
      e.preventDefault();
      const box = e.currentTarget.getBoundingClientRect();
      save(dropOn(ids, drag, id, e.clientY > box.top + box.height / 2));
      setDrag(null);
      setDropAt(null);
    },
    dragEnd() {
      setDrag(null);
      setDropAt(null);
    },
  };
}

export function ContractorsPage() {
  const { me } = useMe();
  // P13: the search, the kind and the open form live in the address ("Wstecz" comes back to them)
  const { params, update } = useUrlState();
  const search = params.get('szukaj') ?? '';
  const setSearch = (v: string) => update({ szukaj: v });
  const kindParam = params.get('rodzaj');
  const kind: '' | Kind = kindParam && kindParam in KIND_LABELS ? (kindParam as Kind) : '';
  const setKind = (v: '' | Kind) => update({ rodzaj: v });
  const creating = params.get('nowy') === '1';
  const setCreating = (v: boolean) => update({ nowy: v ? '1' : null });
  const editingId = params.get('edycja');
  const setEditingId = (id: string | null) => update({ edycja: id });
  const [historyId, setHistoryId] = useState<string | null>(null);
  // review I2: a role that may delete but not edit has no form to delete from — the row's „…” opens the question
  const [deleting, setDeleting] = useState<Contractor | null>(null);
  const q = useDebounced(search.trim(), 250);

  const list = useQuery({
    queryKey: [...CONTRACTORS_KEY, q, kind],
    // Keep the rows (and an open form) on screen while a new search loads.
    placeholderData: keepPreviousData,
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (kind) params.set('kind', kind);
      const qs = params.toString();
      return api<ListResponse>(`/api/contractors${qs ? `?${qs}` : ''}`);
    },
  });
  const order = useOrder(list.data?.contractors);
  if (!me) return null;
  const items = order.items;
  const canOrder = can(me, RESOURCE, 'edit');
  // The server drops the fields a user may not see; every row has the same fields, so the first row tells which are visible.
  // With no rows yet we cannot tell: show everything except the sensitive discount. Owners see everything.
  const sample = items[0];
  const visible = (field: string) => me.isOwner || (sample ? field in sample : field !== 'default_discount_bp');

  return (
    <section className="page" data-feature-id="contractors.list">
      <div className="page-head">
        <h1>Kontrahenci</h1>
        {can(me, RESOURCE, 'create') && !creating && (
          <button className="btn primary" type="button" data-feature-id="contractors.edit" onClick={() => setCreating(true)}>
            Dodaj kontrahenta
          </button>
        )}
      </div>

      {creating && (
        <ContractorForm
          visible={visible}
          onDone={() => {
            dropDraft('contractors.contractor:new');
            setCreating(false);
          }}
        />
      )}

      <div className="filters" data-feature-id="contractors.search">
        <label className="field">
          <span>Szukaj</span>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nazwa, NIP, telefon, e-mail" autoComplete="off" />
        </label>
        <label className="field">
          <span>Rodzaj</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as '' | Kind)}>
            <option value="">Wszyscy</option>
            {(Object.keys(KIND_LABELS) as Kind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && (
        <p className="alert" role="alert">
          {list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać kontrahentów'}
        </p>
      )}
      {list.data && items.length === 0 && <p className="muted">Nie znaleziono kontrahentów.</p>}
      {order.error && (
        <p className="alert" role="alert">
          {order.error}
        </p>
      )}
      {list.data?.truncated && <p className="notice">Pokazano pierwszych 200 kontrahentów. Zawęź wyszukiwanie, żeby zobaczyć pozostałych.</p>}

      <ul className="list">
        {items.map((c, index) => {
          const open = editingId === c.id;
          return (
            <li
              key={c.id}
              className={['row', 'con-row', order.drag === c.id ? 'con-dragging' : '', order.dropAt?.id === c.id && order.drag !== c.id ? (order.dropAt.after ? 'con-drop-after' : 'con-drop-before') : ''].filter(Boolean).join(' ')}
              onDragOver={canOrder ? (e) => order.dragOver(e, c.id) : undefined}
              onDrop={canOrder ? (e) => order.drop(e, c.id) : undefined}
            >
              {canOrder && !open && (
                <span
                  className="con-grip"
                  draggable
                  aria-hidden="true"
                  title="Przeciągnij, żeby zmienić kolejność (albo ↑ / ↓)"
                  data-feature-id="contractors.order"
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', c.id);
                    order.setDrag(c.id);
                  }}
                  onDragEnd={order.dragEnd}
                />
              )}
              <div className="row-main">
                <div className="row-title">
                  <strong>{c.name}</strong>
                  <span className="chip">{KIND_LABELS[c.kind]}</span>
                </div>
                {c.nip && <div className="muted">NIP: {c.nip}</div>}
                {c.phone && <div className="muted">{c.phone}</div>}
                {c.email && <div className="muted">{c.email}</div>}
                {c.default_discount_bp !== undefined && <div className="muted">Rabat: {bpToPercent(c.default_discount_bp)}%</div>}
              </div>
              {!open && (
                <div className="actions row-actions">
                  {infoAvailable(me, c.kind) && <ContractorInfo contractor={c} />}
                  {canOrder && (
                    <span className="con-move">
                      <button className="btn small ghost con-hover" type="button" aria-label={`W górę: ${c.name}`} disabled={index === 0} onClick={() => order.move(c.id, -1)}>
                        ↑
                      </button>
                      <button className="btn small ghost con-hover" type="button" aria-label={`W dół: ${c.name}`} disabled={index === items.length - 1} onClick={() => order.move(c.id, 1)}>
                        ↓
                      </button>
                    </span>
                  )}
                  {can(me, RESOURCE, 'edit') && (
                    <button className="btn small" type="button" data-feature-id="contractors.edit" aria-label={`Edytuj: ${c.name}`} onClick={() => leaveGuarded(() => setEditingId(c.id))}>
                      Edytuj
                    </button>
                  )}
                  {/* review I2: „Usuń” stays off the row; only a role without „Edycja” (no form) gets it under „…” */}
                  {can(me, RESOURCE, 'delete') && !can(me, RESOURCE, 'edit') && (
                    <RowMenu label={`Więcej: ${c.name}`} actions={[{ label: 'Usuń kontrahenta', danger: true, onSelect: () => setDeleting(c) }]} />
                  )}
                  {can(me, 'history.entries', 'view') && (
                    <button className="btn small" type="button" onClick={() => setHistoryId(historyId === c.id ? null : c.id)} aria-expanded={historyId === c.id}>
                      Historia zmian
                    </button>
                  )}
                </div>
              )}
              {open && (
                <ContractorForm
                  contractor={c}
                  visible={visible}
                  canDelete={can(me, RESOURCE, 'delete')}
                  onDone={() => {
                    dropDraft(`contractors.contractor:${c.id}`);
                    setEditingId(null);
                  }}
                />
              )}
              {(open || historyId === c.id) && can(me, 'history.entries', 'view') && <HistoryPanel entityId={c.id} resource={RESOURCE} />}
            </li>
          );
        })}
      </ul>

      {deleting && <DeleteDialog contractor={deleting} onClose={() => setDeleting(null)} onDeleted={() => setDeleting(null)} />}
    </section>
  );
}
